import { ChevronLeftIcon } from './ui'

export interface ClaimsYellowHeaderProps {
  /** Announced by screen readers for the back control */
  backAriaLabel: string
  onBack: () => void
  /** When true, header Back is non-interactive (e.g. while an async submit is in flight). */
  backDisabled?: boolean
  /** Small uppercase line above the title (optional). */
  eyebrow?: string
  /** Primary heading */
  title: string
  /** When false, the header chevron+Back row is omitted (use an inline back link in the page body). */
  showHeaderBack?: boolean
}

/**
 * Shared yellow banner for claim-flow steps (`/claims/start`, `/claims/select-type`, etc.).
 * Matches compact spacing and typography used on the “Make a claim” intro screen.
 */
export default function ClaimsYellowHeader({
  backAriaLabel,
  onBack,
  backDisabled = false,
  eyebrow,
  title,
  showHeaderBack = true,
}: ClaimsYellowHeaderProps) {
  return (
    <header className="bg-foreground-brand-primary-alt flex flex-col gap-0 items-center overflow-clip px-0 py-[12px] md:py-[28px] relative shrink-0 w-full">
      <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
        <div className="flex w-full max-w-[768px] flex-col items-start gap-[10px] md:gap-[16px]">
          {showHeaderBack ? (
            <button
              type="button"
              onClick={onBack}
              disabled={backDisabled}
              aria-disabled={backDisabled}
              className={`flex shrink-0 items-center gap-[4px] overflow-clip p-0 text-text-primary-900 ${
                backDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}
              aria-label={backAriaLabel}
            >
              <ChevronLeftIcon className="size-[18px] shrink-0 md:size-5" aria-hidden />
              <span className="font-body font-semibold text-sm leading-none md:leading-sm">Back</span>
            </button>
          ) : null}

          <div className="flex w-full min-w-0 flex-col items-start gap-[5px] md:gap-[8px]">
            {eyebrow ? (
              <p className="m-0 w-full text-left font-body font-semibold text-xs uppercase tracking-[0.08em] text-text-primary-900 leading-none">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="m-0 w-full text-left font-display font-regular text-xl leading-[26px] text-text-primary-900 md:text-display-sm md:leading-[38px]">
              {title}
            </h1>
          </div>
        </div>
      </div>
    </header>
  )
}
